import { z } from 'zod';
import { CustomerOptionSchema } from './customer.js';
import { UserLanguageEnum, UserRoleEnum, UserStatusEnum } from './user.js';

/**
 * Authentication request/response schemas (ADR-0003).
 */
export const LoginRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  password: z.string().min(1),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

/**
 * Login response. Discriminated by `mfaRequired`:
 *   - `false` -> session cookies are set, body carries the user profile and
 *     access-token TTL (clients use it to schedule a silent refresh).
 *   - `true`  -> MFA is enrolled for this account; no cookies were set, the
 *     `challengeToken` (HMAC, short-lived) binds the next
 *     `/auth/mfa/challenge` call to this user. The challenge endpoint lands
 *     in #24.
 *
 * Note: the previous v0 schema flattened the response into a single record
 * with `accessToken` in the body. We dropped `accessToken` because the JWT
 * lives in the `__Host-iat` cookie and must never be readable by JS, and
 * we kept `expiresIn` for the silent-refresh case.
 */
export const LoginResponseSchema = z.discriminatedUnion('mfaRequired', [
  z.object({
    mfaRequired: z.literal(false),
    user: z.object({
      id: z.string(),
      email: z.string().email(),
      role: UserRoleEnum,
      status: UserStatusEnum,
      language: UserLanguageEnum,
    }),
    /** Seconds until the access-token cookie expires. */
    expiresIn: z.number().int().positive(),
  }),
  z.object({
    mfaRequired: z.literal(true),
    /** Short-lived HMAC token bound to `userId`. Consumed by `/auth/mfa/challenge` (#24). */
    challengeToken: z.string(),
  }),
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const SessionResponseSchema = z.object({
  user: z.object({
    id: z.string(),
    name: z.string().min(1).max(200).nullable(),
    email: z.string().email(),
    role: UserRoleEnum,
    status: UserStatusEnum,
    language: UserLanguageEnum,
    defaultCustomerId: z.string().nullable(),
    customers: z.array(CustomerOptionSchema).optional(),
  }),
});
export type SessionResponse = z.infer<typeof SessionResponseSchema>;

/**
 * Admin-only registration. Self-signup is not part of v1; admins create
 * accounts which the user activates via password-reset email.
 */
export const RegisterRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
  role: UserRoleEnum,
  /** Optional initial password; if omitted, the user must reset before login. */
  initialPassword: z.string().min(8).optional(),
});
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const RefreshRequestSchema = z.object({
  /**
   * Optional body field; the refresh token is normally read from the
   * `__Host-iar` cookie. Body is supported for non-browser clients.
   */
  refreshToken: z.string().optional(),
});
export type RefreshRequest = z.infer<typeof RefreshRequestSchema>;

export const PasswordResetRequestSchema = z.object({
  email: z.string().email().toLowerCase(),
});
export type PasswordResetRequest = z.infer<typeof PasswordResetRequestSchema>;

export const PasswordResetConfirmSchema = z.object({
  /** Signed token from the password-reset email. */
  token: z.string().min(1),
  newPassword: z.string().min(8),
});
export type PasswordResetConfirm = z.infer<typeof PasswordResetConfirmSchema>;

/**
 * MFA setup response. Returned by `POST /auth/mfa/setup`. The plaintext
 * recovery codes are shown to the user EXACTLY once; we never persist them
 * un-hashed (see `mfa_recovery_codes.code_hash`). The `otpauthUri` is the
 * URI authenticator apps consume - the SPA renders a QR code from it
 * client-side.
 */
export const MfaSetupResponseSchema = z.object({
  /** Base32 secret. Echo'd so power-users can paste manually if QR fails. */
  secret: z.string().min(1),
  /** otpauth://totp/Issuer:label?secret=...&issuer=...&algorithm=SHA1&digits=6&period=30 */
  otpauthUri: z.string().min(1),
  /** 10 plaintext recovery codes (`XXXX-YYYY`). */
  recoveryCodes: z.array(z.string()).length(10),
});
export type MfaSetupResponse = z.infer<typeof MfaSetupResponseSchema>;

/** Request body for `POST /auth/mfa/verify`. */
export const MfaVerifyRequestSchema = z.object({
  /** 6-digit TOTP code from the user's authenticator app. */
  otp: z.string().regex(/^\d{6}$/),
});
export type MfaVerifyRequest = z.infer<typeof MfaVerifyRequestSchema>;

/**
 * Request body for `POST /auth/mfa/challenge`. Either `otp` (TOTP from the
 * authenticator app) or `recoveryCode` (one of the 10 setup-time codes) must
 * be present; the route validates exactly one of them. The challenge token
 * comes from the MFA branch of `POST /auth/login`.
 */
export const MfaChallengeRequestSchema = z
  .object({
    challengeToken: z.string().min(1),
    otp: z
      .string()
      .regex(/^\d{6}$/)
      .optional(),
    /** `XXXX-YYYY` format, but we accept any casing / dashes (route normalises). */
    recoveryCode: z.string().min(1).optional(),
  })
  .refine((d) => Boolean(d.otp || d.recoveryCode), {
    message: 'Either otp or recoveryCode is required',
  });
export type MfaChallengeRequest = z.infer<typeof MfaChallengeRequestSchema>;

/**
 * Request body for `POST /auth/mfa/disable`. Re-authenticates with the
 * password to prevent a hijacked-cookie attack from disabling MFA silently;
 * we deliberately do NOT also require an OTP because users who lost their
 * authenticator should be able to disable via password + recovery flow.
 * That trade-off is documented in ADR-0003 follow-up; the route layer
 * audits every disable attempt regardless.
 */
export const MfaDisableRequestSchema = z.object({
  password: z.string().min(1),
});
export type MfaDisableRequest = z.infer<typeof MfaDisableRequestSchema>;
